import api from '@/services/api' // Instancia global de Axios con configuración

export default {
  namespaced: true,
  state: {
    items: [],
    currentPage: 1,
    lastPage: 1,
    totalItems: 0,
    itemsPerPage: 9,
    error: null, // Para manejar errores globales
    fieldErrors: {}, // Para manejar errores específicos de los campos del formulario
    successMessage: null, // Para manejar mensajes de éxito
    errorCode: null // Para capturar el código de error del servidor
  },
  mutations: {
    SET_ERROR(state, error) {
      state.error = error.message || null
      state.errorCode = error.code_number || null
    },
    CLEAR_FIELD_ERRORS(state) {
      state.fieldErrors = {}
    },
    CLEAR_ERROR(state) {
      state.error = null
      state.errorCode = null
      state.fieldErrors = {} // Limpiar errores de campos también
    },
    SET_FIELD_ERRORS(state, errors) {
      state.fieldErrors = errors
    },
    SET_SUCCESS_MESSAGE(state, message) {
      state.successMessage = message
    },
    CLEAR_SUCCESS_MESSAGE(state) {
      state.successMessage = null
    },
    SET_ITEMS(state, items) {
      state.items = Array.isArray(items) ? items : []
    },
    ADD_ITEM(state, item) {
      state.items.unshift(item) // Agregar al inicio
    },
    UPDATE_ITEM(state, item) {
      const index = state.items.findIndex(p => p.id === item.id)
      if (index !== -1) {
        state.items.splice(index, 1, item) // Actualizar manteniendo posición
      }
    },
    DELETE_ITEM(state, itemId) {
      state.items = state.items.filter(item => item.id !== itemId)
    },
    SET_PAGINATION(state, pagination) {
      state.currentPage = pagination.current_page
      state.lastPage = pagination.last_page
      state.totalItems = pagination.total
      state.itemsPerPage = pagination.per_page
    },
    SET_CURRENT_PAGE(state, currentPage) {
      state.currentPage = currentPage
    }
  },
  actions: {
    async fetchRecords({ commit, state }, { resource, options = {} }) {
      const {
        page = state.currentPage,
        search = '',
        itemsPerPage = state.itemsPerPage,
        sortColumn,
        sortOrder
      } = options
      try {
        commit('CLEAR_ERROR')
        const response = await api.get(`/${resource}`, {
          params: {
            page,
            search,
            per_page: itemsPerPage,
            sort_column: sortColumn,
            sort_order: sortOrder
          }
        })
        const { data, pagination } = response.data
        commit('SET_ITEMS', data)
        commit('SET_PAGINATION', pagination)
      } catch (error) {
        commit(
          'SET_ERROR',
          error.response?.data || {
            message: 'Error al cargar los registros',
            code_number: 500
          }
        )
      }
    },

    handleApiError({ commit }, error) {
      const serverError = error.response?.data || {
        message: 'Error inesperado',
        code_number: 500
      }
      commit('SET_ERROR', serverError)
      commit('SET_FIELD_ERRORS', error.response?.data?.errors || {})
      throw new Error(serverError.message)
    },

    async reloadRecords({ dispatch, state }, resource) {
      await dispatch('fetchRecords', {
        resource,
        options: {
          page: state.currentPage,
          search: state.search,
          itemsPerPage: state.itemsPerPage,
          sortColumn: state.sortColumn,
          sortOrder: state.sortOrder
        }
      })
    },

    async addModel({ commit, dispatch }, { resource, itemData }) {
      try {
        commit('CLEAR_ERROR')
        const response = await api.post(`/${resource}`, itemData)
        const { data, message } = response.data
        commit('ADD_ITEM', data)
        commit('SET_SUCCESS_MESSAGE', message)
        await dispatch('reloadRecords', resource)
        return { message, data } // Retornar también los datos creados
      } catch (error) {
        await dispatch('handleApiError', error)
      }
    },

    async editModel({ commit, dispatch }, { resource, itemData }) {
      try {
        commit('CLEAR_ERROR')
        const response = await api.put(`/${resource}/${itemData.id}`, itemData)
        const { data, message } = response.data
        commit('UPDATE_ITEM', data)
        commit('SET_SUCCESS_MESSAGE', message)
        await dispatch('reloadRecords', resource)
        return { message, data } // Retornar también los datos actualizados
      } catch (error) {
        await dispatch('handleApiError', error)
      }
    },

    async deleteRecord({ commit, dispatch }, { resource, itemId }) {
      try {
        commit('CLEAR_ERROR')
        const response = await api.delete(`/${resource}/${itemId}`)
        const { message, deleted_patient, code_number } = response.data
        commit('DELETE_ITEM', itemId)
        commit('SET_SUCCESS_MESSAGE', message)
        await dispatch('reloadRecords', resource)
        return { message, deleted_patient, code_number }
      } catch (error) {
        await dispatch('handleApiError', error)
      }
    }
  },

  getters: {
    items: state => state.items,
    fieldErrors: state => state.fieldErrors,
    error: state => state.error,
    errorCode: state => state.errorCode,
    successMessage: state => state.successMessage,
    totalItems: state => state.totalItems,
    currentPage: state => state.currentPage,
    lastPage: state => state.lastPage
  }
}